import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnInit,
	Output,
	signal,
	Signal,
	computed,
	WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import {
	BusinessAddress,
	BusinessUser,
	BusinessUserRole,
} from '@models/businesses';
import {
	ActingForType,
	MatterBusinessUser,
	MatterFileType,
} from '@models/matter';
import { TrustLedgerDefault, TrustLedgerOrderItem } from '@models/trustLedger';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Select, Store } from '@ngxs/store';
import { MatterRepo } from '@repos/matter/matter.repo';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessAddressesState } from '@state/business/address';
import { MatterDispatch } from '@state/matter';
import { combineLatest, Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { createId } from '@core/util/file';
import { BaseCreateMatterModel } from 'src/app/shared/services/matter/matter.service';
import { CreateStatusType } from '@state/utils/models';
import { openClose } from '@core/animations/animations';
import { cloneDeep } from 'lodash';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessState } from '@state/business';
import { BusinessChecklistsSelectors } from '@state/business/checklists';
import { ChecklistItem } from '@models/checklist';
import { RemoteConfigService } from '@services/remoteConfig/remote-config.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserProfileState } from '@state/user-profile';
import { AuthState } from '@state/auth';
import { TestHooks } from './matter-details.hooks';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

interface SimpleOfficeProfile {
	name: string;
	id: string;
}

@UntilDestroy()
@Component({
	selector: 'app-create-modal-details-step',
	templateUrl: './matter-details.component.html',
	styleUrls: ['./matter-details.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class MatterDetailsStepComponent implements OnInit {
	testHooks = TestHooks;
	public matterId: string = createId();

	@Input() backText = 'Back';
	@Input() matterType: MatterFileType;
	@Input() initialLawyerId: string;
	@Input() disableLawyerSelect = false;

	@Output() goBack = new EventEmitter();
	@Output() onCreate: EventEmitter<[string, BaseCreateMatterModel]> =
		new EventEmitter<[string, BaseCreateMatterModel]>();
	@Output() onClose: EventEmitter<void> = new EventEmitter<void>();

	@Select(SelectedMatter.getCreateStatus)
	createStatus$: Observable<CreateStatusType>;

	public matter: BaseCreateMatterModel;
	public matterTypes = MatterFileType;
	public error = false;

	public fileName: string;
	public fileNumber: string;
	public fileAccountingNumber: string;

	public assignedLawyer: MatterBusinessUser;
	public assignedLawyerName: string;
	public assignedClerk: MatterBusinessUser;
	public assignedClerkName: string;
	public lawyers = [];
	public clerks = [];
	public requireLawyerAndClerk: boolean;
	public actingFor;
	public officesSimpleProfiles_: Signal<SimpleOfficeProfile[]>;
	public offices_: Signal<BusinessAddress[]>;
	public selectedOffice_: WritableSignal<SimpleOfficeProfile | null> =
		signal(null);
	public selectedOfficeDisplayName_: WritableSignal<string> = signal('');
	public showOfficialOfficeName_: WritableSignal<boolean> = signal(false);
	public selectedOfficeOfficialName_: WritableSignal<string> = signal('');

	public submitted = false;
	public creating = false;
	public lastestMatter;
	public ICON = ICON;
	private userId: string;

	actingForTypes = ActingForType;

	@Select(BusinessUsersState.getLawyers) lawyers$: Observable<BusinessUser[]>;
	@Select(BusinessUsersState.getClerks) clerks$: Observable<BusinessUser[]>;
	@Select(AuthState.getUserId) userId$: Observable<string>;
	@Select(BusinessUsersState.getLoggedInUserRole)
	loggedInUserRole$: Observable<BusinessUserRole>;

	loggedInUser_ = select(BusinessUsersState.getLoggedInBusinessUser);

	@SelectSnapshot(UserProfileState.getProfileFullName) profileFullName: string;

	@SelectSnapshot(BusinessState.getDefaultTrustLedgerPaymentsSale)
	public defaultTrustLedgerPaymentsSale: {
		[id: string]: TrustLedgerDefault;
	};
	@SelectSnapshot(BusinessState.getDefaultTrustLedgerPaymentsSaleOrder)
	public defaultTrustLedgerPaymentsSaleOrder: {
		[id: string]: TrustLedgerOrderItem;
	};

	@SelectSnapshot(BusinessState.getDefaultTrustLedgerPaymentsPurchase)
	public defaultTrustLedgerPaymentsPurchase: {
		[id: string]: TrustLedgerDefault;
	};
	@SelectSnapshot(BusinessState.getDefaultTrustLedgerPaymentsPurchaseOrder)
	public defaultTrustLedgerPaymentsPurchaseOrder: {
		[id: string]: TrustLedgerOrderItem;
	};

	@SelectSnapshot(BusinessState.getDefaultTrustLedgerPaymentsRefinance)
	public defaultTrustLedgerPaymentsRefinance: {
		[id: string]: TrustLedgerDefault;
	};
	@SelectSnapshot(BusinessState.getDefaultTrustLedgerPaymentsRefinanceOrder)
	public defaultTrustLedgerPaymentsRefinanceOrder: {
		[id: string]: TrustLedgerOrderItem;
	};

	@SelectSnapshot(BusinessState.getDefaultTrustLedgerReceiptsSale)
	public defaultTrustLedgerReceiptsSale: {
		[id: string]: TrustLedgerDefault;
	};
	@SelectSnapshot(BusinessState.getDefaultTrustLedgerReceiptsSaleOrder)
	public defaultTrustLedgerReceiptsSaleOrder: {
		[id: string]: TrustLedgerOrderItem;
	};

	@SelectSnapshot(BusinessState.getDefaultTrustLedgerReceiptsPurchase)
	public defaultTrustLedgerReceiptsPurchase: {
		[id: string]: TrustLedgerDefault;
	};
	@SelectSnapshot(BusinessState.getDefaultTrustLedgerReceiptsPurchaseOrder)
	public defaultTrustLedgerReceiptsPurchaseOrder: {
		[id: string]: TrustLedgerOrderItem;
	};

	@SelectSnapshot(BusinessState.getDefaultTrustLedgerReceiptsRefinance)
	public defaultTrustLedgerReceiptsRefinance: {
		[id: string]: TrustLedgerDefault;
	};
	@SelectSnapshot(BusinessState.getDefaultTrustLedgerReceiptsRefinanceOrder)
	public defaultTrustLedgerReceiptsRefinanceOrder: {
		[id: string]: TrustLedgerOrderItem;
	};

	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistItemsSale)
	public defaultChecklistSale: ChecklistItem[];
	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistItemsPurchase)
	public defaultChecklistPurchase: ChecklistItem[];
	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistItemsRefinance)
	public defaultChecklistRefinance: ChecklistItem[];

	offices$: Observable<BusinessAddress[]> = inject(Store).select(
		BusinessAddressesState.getAll,
	);
	primaryOfficeId_ = select(BusinessAddressesState.primaryId);

	constructor(
		private matterRepo: MatterRepo,
		private matterDispatch: MatterDispatch,
		private remoteConfigService: RemoteConfigService,
		private store: Store,
	) {
		this.matterDispatch.CreateReset();

		this.offices_ = toSignal(this.offices$, { initialValue: [] });

		this.officesSimpleProfiles_ = computed(() => {
			const availableOffices = this.offices_();

			if (!availableOffices) return [];

			const officesOptions = availableOffices.map((office) => ({
				id: office.id,
				name: office.officeNickName || office.officeName,
			}));

			return officesOptions;
		});
	}

	ngOnInit() {
		combineLatest([
			this.userId$,
			this.loggedInUserRole$,
			this.lawyers$,
			this.clerks$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(([loggedInUserId, loggedInUserRole, lawyers, clerks]) => {
				this.userId = loggedInUserId;

				const _lawyers = [{ name: 'No lawyer selected', id: null }];
				const _clerks = [{ name: 'No clerk selected', id: null }];

				if (this.initialLawyerId) {
					const _lawyer = lawyers.find((x) => x.id === this.initialLawyerId);
					if (_lawyer) {
						this.assignedLawyer = {
							name: `${_lawyer.firstName} ${_lawyer.lastName}`,
							id: _lawyer.id,
						};
						this.assignedLawyerName = `${_lawyer.firstName} ${_lawyer.lastName}`;
					}
				}

				if (
					!this.assignedLawyer &&
					loggedInUserRole === BusinessUserRole.LAWYER
				) {
					const _lawyer = lawyers.find(
						(lawyer) => lawyer.id === loggedInUserId,
					);
					if (_lawyer) {
						this.assignedLawyer = {
							name: `${_lawyer.firstName} ${_lawyer.lastName}`,
							id: _lawyer.id,
						};
						this.assignedLawyerName = this.assignedLawyer.name;
					}
				} else if (loggedInUserRole === BusinessUserRole.CLERK) {
					const _clerk = clerks.find((clerk) => clerk.id === loggedInUserId);
					if (_clerk) {
						this.assignedClerk = {
							name: `${_clerk.firstName} ${_clerk.lastName}`,
							id: _clerk.id,
						};
						this.assignedClerkName = this.assignedClerk.name;
					}
				}

				if (lawyers.length === 1 && !this.assignedLawyer?.id) {
					const tmpLawyer = {
						name: `${lawyers[0].firstName} ${lawyers[0].lastName}`,
						id: lawyers[0].id,
					};
					this.assignedLawyer = tmpLawyer;
					this.assignedLawyerName = tmpLawyer.name;
				}

				if (clerks.length === 1 && !this.assignedClerk?.id) {
					const tmpClerk = {
						name: `${clerks[0].firstName} ${clerks[0].lastName}`,
						id: clerks[0].id,
					};
					this.assignedClerk = tmpClerk;
					this.assignedClerkName = tmpClerk.name;
				}
				clerks.forEach((clerk) => {
					_clerks.push({
						name: `${clerk.firstName} ${clerk.lastName}`,
						id: clerk.id,
					});
				});

				lawyers.forEach((lawyer) => {
					_lawyers.push({
						name: `${lawyer.firstName} ${lawyer.lastName}`,
						id: lawyer.id,
					});
				});

				this.lawyers = cloneDeep(_lawyers);
				this.clerks = cloneDeep(_clerks);
			});

		this.matterRepo
			.GetLatestMatter()
			.pipe(untilDestroyed(this))
			.subscribe((lastestMatter) => {
				this.lastestMatter = lastestMatter[0];
			});

		this.createStatus$.pipe(untilDestroyed(this)).subscribe((status) => {
			if (this.submitted && status === CreateStatusType.success) {
				this.onCreate.emit([this.matterId, this.matter]);
			} else if (this.submitted && status === CreateStatusType.failed) {
				this.error = true;
			}

			this.creating = false;
		});

		this.setInitialOffice();
	}

	async submit(form: NgForm): Promise<void> {
		this.error = null;

		if (form.valid) {
			const matter: BaseCreateMatterModel = {
				name: this.fileName,
				number: this.fileNumber,
				lawyerAssigned: this.assignedLawyer ?? null,
				clerkAssigned: this.assignedClerk ?? null,
				fileType: this.matterType,
				officeId: this.selectedOffice_()?.id ?? null,
				actingFor: this.actingFor ?? null,

				// TODO: Once we have fully rolled out C# matter creation for all then we don't need createdBy. This is only used by the old code. The C# code will determine this in
				//       the backend.
				createdBy: { name: this.profileFullName, id: this.userId },
			};

			if (await this.remoteConfigService.useCSharpMatter()) {
				console.log('Using C# for matter creation...');
				this.matter = matter;
				this.creating = true;
				this.matterDispatch.Create(this.matterId, matter);
			} else {
				console.log('Using old code for matter creation...');
				let defaultTrustLedgerPayments;
				let defaultTrustLedgerReceipts;
				let defaultTrustLedgerPaymentsOrder;
				let defaultTrustLedgerReceiptsOrder;
				let defaultChecklistItems;
				switch (this.matterType) {
					case MatterFileType.SALE:
						defaultTrustLedgerPayments = this.defaultTrustLedgerPaymentsSale;
						defaultTrustLedgerPaymentsOrder =
							this.defaultTrustLedgerPaymentsSaleOrder;
						defaultTrustLedgerReceipts = this.defaultTrustLedgerReceiptsSale;
						defaultTrustLedgerReceiptsOrder =
							this.defaultTrustLedgerReceiptsSaleOrder;
						defaultChecklistItems = this.defaultChecklistSale;
						break;
					case MatterFileType.PURCHASE:
						defaultTrustLedgerPayments =
							this.defaultTrustLedgerPaymentsPurchase;
						defaultTrustLedgerPaymentsOrder =
							this.defaultTrustLedgerPaymentsPurchaseOrder;
						defaultTrustLedgerReceipts =
							this.defaultTrustLedgerReceiptsPurchase;
						defaultTrustLedgerReceiptsOrder =
							this.defaultTrustLedgerReceiptsPurchaseOrder;
						defaultChecklistItems = this.defaultChecklistPurchase;
						break;
					case MatterFileType.REFINANCE:
						defaultTrustLedgerPayments =
							this.defaultTrustLedgerPaymentsRefinance;
						defaultTrustLedgerPaymentsOrder =
							this.defaultTrustLedgerPaymentsRefinanceOrder;
						defaultTrustLedgerReceipts =
							this.defaultTrustLedgerReceiptsRefinance;
						defaultTrustLedgerReceiptsOrder =
							this.defaultTrustLedgerReceiptsRefinanceOrder;
						defaultChecklistItems = this.defaultChecklistRefinance;
						break;
				}

				this.matter = matter;
				this.creating = true;
				this.matterDispatch.CreateJs(
					this.matterId,
					matter,
					defaultTrustLedgerPayments,
					defaultTrustLedgerPaymentsOrder,
					defaultTrustLedgerReceipts,
					defaultTrustLedgerReceiptsOrder,
					defaultChecklistItems,
				);
			}
		}

		this.submitted = true;
	}

	setLawyer(lawyer) {
		if (lawyer?.result?.id) {
			this.assignedLawyer = lawyer.result;
			this.assignedLawyerName = lawyer.result.name;
		} else {
			this.assignedLawyer = null;
			this.assignedLawyerName = null;
		}
	}

	setClerk(clerk) {
		if (clerk?.result?.id) {
			this.assignedClerk = clerk.result;
			this.assignedClerkName = clerk.result.name;
		} else {
			this.assignedClerk = null;
			this.assignedClerkName = null;
		}
	}

	cancel = () => {
		this.goBack.emit();
	};

	closeModal() {
		this.onClose.emit();
	}

	setInitialOffice() {
		const officeIdAssociatedWithUser = this.loggedInUser_()?.associatedOfficeId;

		// First check if the user has an associated office, if so, preselect it
		if (officeIdAssociatedWithUser) {
			const matchedOffice = this.officesSimpleProfiles_().find(
				(office) => office.id === officeIdAssociatedWithUser,
			);

			if (matchedOffice) {
				this.setOffice(matchedOffice);
				return;
			}
		}

		// If the user has no associated office but there is only one office in the business, then preselect it as the default
		if (this.officesSimpleProfiles_()?.length === 1) {
			const soleOffice = this.officesSimpleProfiles_()[0];
			this.setOffice(soleOffice);
			return;
		}

		// If there are multiple offices, then preselect the primary office
		const primaryOfficeId = this.primaryOfficeId_();
		if (primaryOfficeId) {
			const primaryOfficeSimpleProfile = this.officesSimpleProfiles_().find(
				(x) => x.id === primaryOfficeId,
			);
			this.setOffice(primaryOfficeSimpleProfile);
			return;
		}
	}

	setOffice(selectedOffice: SimpleOfficeProfile) {
		if (!selectedOffice?.id) {
			this.selectedOffice_.set(null);
			this.selectedOfficeDisplayName_.set('');
			this.showOfficialOfficeName_.set(false);
			this.selectedOfficeOfficialName_.set('');
			return;
		}

		this.selectedOffice_.set(selectedOffice);
		this.selectedOfficeDisplayName_.set(selectedOffice.name);

		const allOffices = this.offices_();
		const officeData = allOffices.find((x) => x.id === selectedOffice.id);

		if (officeData?.officeNickName) {
			this.showOfficialOfficeName_.set(true);
			this.selectedOfficeOfficialName_.set(officeData.officeName);
		} else {
			this.showOfficialOfficeName_.set(false);
			this.selectedOfficeOfficialName_.set('');
		}
	}

	onOfficeSelect(event: DropdownResult<SimpleOfficeProfile>) {
		this.setOffice(event.result);
	}
}
